import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { X, Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Fluent 2 Toast Component
 * https://fluent2.microsoft.design/components/web/react/core/toast/usage
 * Position: top-right, border-left accent color, white/dark background
 */
const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-neutral-200 bg-white p-4 pr-8 shadow-[0_4px_8px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)] transition-all dark:border-neutral-800 dark:bg-neutral-900',
  {
    variants: {
      variant: {
        default: 'border-l-4 border-l-[#0c00ff] dark:border-l-[#479ef5]',
        info: 'border-l-4 border-l-[#0c00ff] dark:border-l-[#479ef5]',
        success: 'border-l-4 border-l-[#0e7a0d] dark:border-l-[#54b054]',
        warning: 'border-l-4 border-l-[#9d5d00] dark:border-l-[#f9a825]',
        destructive: 'border-l-4 border-l-[#c50f1f] dark:border-l-[#dc626d]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const variantIcons = {
  default: Info,
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  destructive: XCircle,
}

const variantIconColors = {
  default: 'text-[#0c00ff] dark:text-[#479ef5]',
  info: 'text-[#0c00ff] dark:text-[#479ef5]',
  success: 'text-[#0e7a0d] dark:text-[#54b054]',
  warning: 'text-[#9d5d00] dark:text-[#f9a825]',
  destructive: 'text-[#c50f1f] dark:text-[#dc626d]',
}

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof toastVariants> {
  onOpenChange?: (open: boolean) => void
  open?: boolean
  showIcon?: boolean
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant = 'default', children, onOpenChange: _onOpenChange, open = true, showIcon = true, ...props }, ref) => {
    if (!open) return null

    const Icon = variantIcons[variant || 'default']
    const iconColor = variantIconColors[variant || 'default']

    return (
      <div
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      >
        {showIcon && <Icon className={cn('h-5 w-5 flex-shrink-0', iconColor)} />}
        <div className="flex-1">{children}</div>
      </div>
    )
  }
)
Toast.displayName = 'Toast'

const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>
}

const ToastViewport = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 sm:max-w-[420px]',
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = 'ToastViewport'

const ToastTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm font-semibold text-neutral-900 dark:text-neutral-100', className)} {...props} />
))
ToastTitle.displayName = 'ToastTitle'

const ToastDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm text-neutral-600 dark:text-neutral-400', className)} {...props} />
))
ToastDescription.displayName = 'ToastDescription'

const ToastClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-md p-1 text-neutral-500 opacity-0 transition-opacity',
      'hover:text-neutral-900 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#0c00ff]',
      'group-hover:opacity-100',
      'dark:text-neutral-400 dark:hover:text-neutral-100 dark:focus:ring-[#479ef5]',
      className
    )}
    {...props}
  >
    <X className="h-4 w-4" />
  </button>
))
ToastClose.displayName = 'ToastClose'

const ToastAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-neutral-300 bg-transparent px-3 text-sm font-medium transition-colors',
      'hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-[#0c00ff]',
      'disabled:pointer-events-none disabled:opacity-50',
      'dark:border-neutral-600 dark:hover:bg-neutral-800 dark:focus:ring-[#479ef5]',
      className
    )}
    {...props}
  />
))
ToastAction.displayName = 'ToastAction'

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}